import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Tabs, Tab, Alert } from '@mui/material';

// Configs
import { createAuditsConfig } from '../config/auditsConfig';
import { useReadyAuditsConfig } from '../config/readyAuditsConfig';
import { useArchivedAuditsConfig } from '../config/archivedAuditsConfig';

// Components
import { DataManagementTab } from '@shared/components/data-management';
import { StartAuditConfirmDialog } from '../components/StartAuditConfirmDialog';

// Services
import { notificationService } from '@shared/components/notifications/notificationService';

// DTOs
import { UserRole } from '@features/user-management/dtos';
import { AuditUserRole } from '../dtos';

// Stores
import { useContextStore } from '../../../stores/contextStore';
import { useAuthStore } from '@features/authentication/store/authStore';
import { useSetPageActions } from '../../../contexts/PageActionsContext';

export const SurveyAuditListPage: React.FC = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState<0 | 1 | 2>(0);
  const { setHelp } = useSetPageActions();

  useEffect(() => {
    setHelp({
      title: 'Audits Guide',
      sections: [
        {
          title: 'Pending',
          content:
            'Audits in progress. Review responses, flag inappropriate content, and publish when complete to generate reports.',
        },
        {
          title: 'Done',
          content:
            'Completed and published audits with generated reports. View historical audit data and reports.',
        },
        {
          title: 'Archived',
          content:
            'Audits that were manually archived before completion. These were skipped or ignored without publishing.',
        },
      ],
    });
  }, [setHelp]);

  // Confirmation dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState<{
    id: string;
    surveyInstanceId: string;
    surveyId: string;
    surveyTitle: string;
    responseCount: number;
  } | null>(null);

  // Navigation and context
  const navigate = useNavigate();
  const { activeSubjectId, activeTenantId, activeDomainId } = useContextStore();
  const { userSession } = useAuthStore();

  // Handler for start audit confirmation
  const handleStartAuditClick = (audit: {
    id: string;
    surveyInstanceId: string;
    surveyId: string;
    surveyTitle: string;
    responseCount: number;
  }) => {
    setSelectedAudit(audit);
    setConfirmDialogOpen(true);
  };

  // All hook-based configs must be called before any early returns
  const { config: readyAuditsConfig, startAuditMutation } = useReadyAuditsConfig(
    activeSubjectId ?? '',
    activeTenantId ?? '',
    activeDomainId ?? '',
    navigate,
    handleStartAuditClick
  );

  const auditsConfig = createAuditsConfig(
    activeSubjectId ?? '',
    activeTenantId ?? '',
    activeDomainId ?? '',
    navigate
  );

  const archivedAuditsConfig = useArchivedAuditsConfig(
    activeSubjectId ?? '',
    activeTenantId ?? '',
    activeDomainId ?? '',
    navigate
  );

  // Handle no subject selected state
  if (!activeSubjectId || !activeTenantId || !activeDomainId) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="info">Please select a subject to view audits.</Alert>
      </Container>
    );
  }

  const handleConfirmStartAudit = async () => {
    if (!selectedAudit) return;

    setConfirmDialogOpen(false);

    try {
      // Get user role - only PROFESSOR and COURSE_REPRESENTATIVE can audit
      const currentRole = userSession?.currentRoles?.[0];
      const validAuditRole: AuditUserRole =
        currentRole === UserRole.PROFESSOR || currentRole === UserRole.COURSE_REPRESENTATIVE
          ? (currentRole as AuditUserRole)
          : UserRole.PROFESSOR;

      const newAudit = await startAuditMutation.mutateAsync({
        surveyInstanceId: selectedAudit.surveyInstanceId,
        surveyId: selectedAudit.surveyId,
        userRole: validAuditRole,
      });

      notificationService.success(`Audit started for "${selectedAudit.surveyTitle}"`);

      if (navigate) {
        navigate(`/app/survey-audits/${newAudit.id}`);
      }
    } catch (error) {
      console.error('Failed to start audit:', error);
      notificationService.error('Failed to start audit. Please try again.');
    } finally {
      setSelectedAudit(null);
    }
  };

  const handleCancelStartAudit = () => {
    setConfirmDialogOpen(false);
    setSelectedAudit(null);
  };

  // Tab change handler
  const handleTabChange = (_: React.SyntheticEvent, newValue: 0 | 1 | 2) => {
    setActiveTab(newValue);
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5, flexShrink: 0 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Pending" />
          <Tab label="Done" />
          <Tab label="Archived" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          '& .MuiTableCell-root:nth-of-type(1)': {
            width: '30%',
          },
          '& .MuiTableCell-root:nth-of-type(2)': {
            width: '15%',
          },
          '& .MuiTableCell-root:nth-of-type(3)': {
            width: '10%',
          },
          '& .MuiTableCell-root:nth-of-type(4)': {
            width: '20%',
          },
        }}
      >
        {activeTab === 0 && <DataManagementTab config={readyAuditsConfig} />}
        {activeTab === 1 && <DataManagementTab config={auditsConfig} />}
        {activeTab === 2 && <DataManagementTab config={archivedAuditsConfig} />}
      </Box>

      {/* Start Audit Confirmation Dialog */}
      {selectedAudit && (
        <StartAuditConfirmDialog
          open={confirmDialogOpen}
          onClose={handleCancelStartAudit}
          onConfirm={handleConfirmStartAudit}
          surveyTitle={selectedAudit.surveyTitle}
          responseCount={selectedAudit.responseCount}
        />
      )}
    </Container>
  );
};
